import { Html } from "@react-three/drei";
import type { Euler, Vector3 } from "@react-three/fiber";
import type { FinishItemPostData } from "@tosize/shapes/src/types/workpiece";
import { formatNumber } from "@tosize/ui";
import { Fragment, useState } from "react";
import { useIntl } from "react-intl";
import { match } from "ts-pattern";
import type { ExtendedVisuProps } from "../..";
import { usePanelVisualisation } from "../../Context";

export function Drillholes({ currentField }: Pick<ExtendedVisuProps, "currentField">) {
	const { workpiece, length, width, thickness } = usePanelVisualisation();
	const holes = Object.entries(workpiece.finishes.side);

	return (
		<Fragment>
			{holes.map(([side, finishes]) => (
				<Fragment key={side}>
					{Object.entries(finishes)
						.filter(([code]) => code === "boorgat_plain" || code === "boorgat_side")
						.map(([code, holes]) => (
							<Fragment key={code}>
								{holes.map((hole, key) => (
									<Drillhole
										key={key}
										side={side}
										thickness={thickness}
										length={length}
										width={width}
										hole={hole}
										active={currentField ? currentField === side : true}
									/>
								))}
							</Fragment>
						))}
				</Fragment>
			))}
		</Fragment>
	);
}

interface DrillholeProps {
	hole: FinishItemPostData;
	thickness: number;
	length: number;
	width: number;
	side: string;
	active: boolean;
}

function Drillhole({ hole, side, active, ...props }: DrillholeProps) {
	const [hovered, setHover] = useState(false);
	const intl = useIntl();

	if (
		hole.option_parameters.offset_x === undefined ||
		hole.option_parameters.offset_y === undefined ||
		hole.option_parameters.depth === undefined
	) {
		return null;
	}

	const offset_x = Number(hole.option_parameters.offset_x) / 1000;
	const offset_y = Number(hole.option_parameters.offset_y) / 1000;
	const depth = Number(hole.option_parameters.depth) / 1000;
	const diameter = Number(hole.option.split("_").at(-1)) / 1000;
	const thickness = props.thickness;

	const length = props.length / 1000;
	const width = props.width / 1000;

	const x = match(side)
		.returnType<number>()
		.with("top", () => offset_x)
		.with("bottom", () => length - offset_x)
		.with("length1", () => length - offset_x)
		.with("length2", () => offset_x)
		.with("width1", () => depth)
		.with("width2", () => length - depth)
		.otherwise(() => 0);

	const y = match(side)
		.returnType<number>()
		.with("top", () => width - offset_y)
		.with("bottom", () => width - offset_y)
		.with("length2", () => depth)
		.with("length1", () => width - depth)
		.with("width2", () => offset_x)
		.with("width1", () => width - offset_x)
		.otherwise(() => 0);

	const z = match(side)
		.returnType<number>()
		.with("top", () => thickness - depth / 2)
		.with("bottom", () => depth / 2)
		.with("length1", "length2", "width1", "width2", () => thickness - offset_y)
		.otherwise(() => 0);

	const rotation = match(side)
		.returnType<Euler>()
		.with("top", () => [Math.PI / 2, 0, 0])
		.with("bottom", () => [Math.PI / 2, 0, Math.PI])
		.with("length2", () => [Math.PI, 0, 0])
		.with("length1", () => [0, 0, 0])
		.with("width1", () => [0, 0, Math.PI / 2])
		.with("width2", () => [0, Math.PI, Math.PI / 2])
		.otherwise(() => [0, 0, 0]);

	const scale = match(side)
		.returnType<Vector3>()
		.with("length1", "length2", "width1", "width2", () => [2, 2, 1])
		.otherwise(() => [2, 1, 2]);

	return (
		<Fragment>
			<group
				scale={scale}
				position={[x, y, z]}
				rotation={rotation}
				onPointerOver={() => active && setHover(true)}
				onPointerOut={() => active && setHover(false)}
			>
				<mesh position={[0, depth, 0]}>
					<meshStandardMaterial color={hovered ? "red" : "lime"} />
					<cylinderGeometry args={[0.002, 0.002, depth, 32, 1]} />
				</mesh>

				<mesh>
					<meshStandardMaterial color={hovered ? "gray" : !active ? "silver" : "black"} />
					<cylinderGeometry args={[diameter, diameter, depth, 32, 1]} />
				</mesh>
			</group>

			{hovered && (
				<Html
					position={[x, y, z]}
					className="flex min-w-[125px] flex-col gap-1 rounded bg-white p-2 text-sm shadow"
				>
					{[
						{
							label: "X",
							value: formatNumber({
								lang: intl.locale,
								value: Number(hole.option_parameters.offset_x) / 10,
								style: "unit",
								unit: "centimeter",
							}),
						},
						{
							label: "Y",
							value: formatNumber({
								lang: intl.locale,
								value: Number(hole.option_parameters.offset_y) / 10,
								style: "unit",
								unit: "centimeter",
							}),
						},
						{
							label: intl.formatMessage({ defaultMessage: "Diameter", id: "9qyhXb" }),
							value: formatNumber({
								lang: intl.locale,
								value: diameter * 1000,
								style: "unit",
								unit: "millimeter",
							}),
						},
						{
							label: intl.formatMessage({ defaultMessage: "Diepte", id: "N2FYOk" }),
							value: formatNumber({
								lang: intl.locale,
								value: Number(hole.option_parameters.depth),
								style: "unit",
								unit: "millimeter",
							}),
						},
					].map(({ label, value }, key) => (
						<div key={key}>
							<span className="font-bold">{label}:</span> {value}
						</div>
					))}
				</Html>
			)}
		</Fragment>
	);
}
